// src/modules/media/useAudioControl.ts
import { Options, Vue } from "vue-class-component";
import { audio as audioHost } from "@/config/host.config";
import { booksoteka as bookHost } from "@/config/host.config";
import { auth } from "@/config/host.config";
import { AccessToken } from "@/api/accessDecoder";
import { useAudio } from "@/store/modules/media/audio-list";
import { useVRoom } from "@/store/modules/hotel/virtual-room";

import type { UseAudioList } from "@/@types/audio/use_audio";
import type { VRAudiobooksStatus } from "@/@types/vroom/virtualRooma";
import { computed, provide } from "vue";
import { audioEmitter, isPlayingKey } from "@/composables/audio/useAudioControls";

@Options({
  components: {},
})
class useAudioControl extends Vue {
  audio: HTMLAudioElement | null = null;

  useAudio = useAudio();

  currentTrackIndex = 0;
  currentTime = 0;
  currentTrackDuration = 0;
  duration = 0;
  lastSyncVroom = 0;

  repeat = false;
  shuffle = false;

  get trackList(): UseAudioList[] {
    return this.useAudio.getTrackList;
  }

  get currentTrack(): UseAudioList | null {
    const id = this.useAudio.getCurrentTrack;
    const track = this.trackList.find((el) => el._id === id);
    return track ? track : null;
  }

  get getAudioSRC(): string {
    const token = AccessToken.access;

    if (!this.currentTrack || !token) return "";

    const baseUrl =
      this.currentTrack.src?.path === "book"
        ? `${bookHost.stream}?audiobookId=${this.currentTrack.path}&chapterId=${this.currentTrack._id}`
        : `${audioHost.audio_id3_stream}?id=${this.currentTrack._id}`;

    // return `${baseUrl}&Authorization=${encodeURIComponent(`Bearer ${token}`)}`;
    return `${baseUrl}&Authorization=${`Bearer ${token}`}`;
  }

  get isPlaying(): boolean {
    return this.useAudio.isPlaying;
  }

  beforeMount(): void {
    provide(
      isPlayingKey,
      computed(() => this.isPlaying)
    );
    audioEmitter.on("play", () => this.play());
    audioEmitter.on("pause", () => this.pause());
    audioEmitter.on("next", () => this.next());
    audioEmitter.on("prev", () => this.prevTrack());
    audioEmitter.on("setCurrentTime", (value) => this.setCurrentTime(value));
    audioEmitter.on("sendOnTv", (payload) => this.sendCommand("setPlaylist", payload));
  }

  beforeUnmount(): void {
    this.useAudio.setPlayerState(false);
    this.syncVroomAudiobooks();
    this.useAudio.destroyedPlayer();
    this.destroyedPlayer();
  }

  syncVroomAudiobooks(): void {
    const track = this.currentTrack;
    if (!track || track.src?.path !== "book") return;
    const roomaId = useVRoom().roomaID;
    if (!roomaId || !this.currentTime) return;

    const entry: VRAudiobooksStatus = {
      book_id: track.path,
      filename: track._id,
      current_time: String(Math.floor(this.currentTime)),
      active: this.isPlaying,
    };
    useVRoom().setRvoomaAudiobooks(entry);
    this.$axios
      .put(`${auth.hello}/${roomaId}`, { audiobooks: [entry] })
      .catch((e: unknown) => this.$error(e));
  }

  async openAudio() {
    this.prepareAudio();
    await this.play();
  }

  prepareAudio(): void {
    if (!this.audio) this.createAudio();

    this.pause();
    this.applyAudioSource();
    this.setCurrentTime(this.currentTrack?.currentTime || 0);
  }

  createAudio() {
    if (this.audio) return;

    this.audio = new Audio();
    this.setupAudioListeners();
  }

  async play(): Promise<void> {
    if (!this.audio || !this.audio.src) return;

    try {
      await this.audio.play();
      this.useAudio.setPlayerState(true);
    } catch (error) {
      this.useAudio.setPlayerState(false);
    }
  }

  pause(): void {
    if (this.audio) this.audio.pause();
    this.useAudio.setPlayerState(false);
  }

  setCurrentTime(time: number): void {
    if (!this.audio) return;
    this.audio.currentTime = time;
    this.currentTime = time;
  }

  // Метод для применения src к audio элементу
  applyAudioSource(): void {
    if (!this.audio || !this.getAudioSRC) return;
    this.useAudio.setPlayerState(false);
    this.currentTime = 0;

    this.audio.src = this.getAudioSRC;
    this.audio.load();
  }

  setupAudioListeners(): void {
    if (!this.audio) return;

    console.log("🎵 Настройка слушателей событий");

    this.audio.ontimeupdate = () => {
      if (!this.audio) return;

      this.currentTime = this.audio.currentTime;
      this.useAudio.setCurrentTime(this.audio.currentTime);

      const now = Date.now();
      if (now - this.lastSyncVroom >= 5000) {
        this.lastSyncVroom = now;
        this.syncVroomAudiobooks();
      }
    };

    this.audio.onloadedmetadata = () => {
      if (!this.audio) return;

      this.duration = this.audio.duration;
      this.useAudio.setDuration(this.audio.duration);

      const trackTime = this.currentTrack?.currentTime || 0;
      if (trackTime > 0) {
        this.audio.currentTime = trackTime;
        this.currentTime = trackTime;
        this.useAudio.setCurrentTime(trackTime);
      }
    };

    this.audio.oncanplay = () => {
      console.log("🎵 Аудио готово к воспроизведению");
    };

    this.audio.onplay = () => {
      console.log("🎵 onplay сработал");
      // @ts-expect-error ReactNativeWebView untyped
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: "MEDIA_STATE",
          isPlaying: true,
          isPlaylist: true,
          isVideo: false,
        })
      );

      this.useAudio.setPlayerState(true);
    };

    this.audio.onpause = () => {
      console.log("🎵 onpause сработал");
      this.useAudio.setPlayerState(false);
      this.syncVroomAudiobooks();
    };

    this.audio.onerror = (e) => {
      console.error("🎵 Ошибка воспроизведения:", this.audio?.error, e);
      this.useAudio.setPlayerState(false);
    };

    this.audio.onended = () => {
      console.log("🎵 onended сработал");
      // @ts-expect-error ReactNativeWebView untyped
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: "TRACK_ENDED",
        })
      );
      this.currentTime = 0;
      this.useAudio.setPlayerState(false);
      this.next();
    };

    // Добавляем дополнительные события для отладки
    this.audio.oncanplaythrough = () => {
      console.log("🎵 oncanplaythrough: весь трек загружен");
    };

    this.audio.onwaiting = () => {
      console.log("🎵 onwaiting: ожидание данных");
    };
  }

  next(): void {
    if (this.shuffle) {
      this.selectRandomTrack();
    } else if (this.repeat) {
      if (this.audio) {
        this.audio.pause();
        this.currentTime = 0;
        this.audio.currentTime = 0;
        this.audio.play();
      }
    } else {
      const i = this.trackList.findIndex((el) => el._id === this.currentTrack?._id) + 1;
      this.setTrack(i < this.trackList.length ? i : 0);
    }
  }

  nextTrack(): void {
    if (this.shuffle) {
      this.selectRandomTrack();
    }
    if (this.audio) {
      this.audio.pause();
      this.currentTime = 0;
      this.audio.currentTime = 0;
      this.audio.play();
    } else {
      const i = this.trackList.findIndex((el) => el._id === this.currentTrack?._id) + 1;
      this.setTrack(i < this.trackList.length ? i : 0);
    }
  }

  prevTrack(): void {
    if (this.shuffle) {
      this.selectRandomTrack();
    } else {
      const i = this.trackList.findIndex((el) => el._id === this.currentTrack?._id) - 1;
      this.setTrack(i >= 0 ? i : this.trackList.length - 1);
    }
  }

  setVolume(persent: number): void {
    if (!this.audio) return;
    this.audio.volume = persent / 100;
  }

  selectRandomTrack(): void {
    let newIndex: number;
    do {
      newIndex = Math.floor(Math.random() * this.trackList.length);
    } while (
      this.trackList[newIndex]._id === this.currentTrack?._id &&
      this.trackList.length > 1
    );
    this.setTrack(newIndex);
  }

  setTrack(newIndex: number): void {
    this.useAudio.setCurrentTrack(this.trackList[newIndex]._id);
    this.$nextTick(() => this.openAudio());
  }

  seekTrack(percentage: number): void {
    if (!this.audio) return;
    this.currentTime = this.audio.duration * percentage;
    if (this.audio) this.audio.currentTime = this.currentTime;
  }

  destroyedPlayer(): void {
    console.log("🎵 Уничтожение плеера");

    if (this.audio) {
      // Останавливаем воспроизведение
      this.useAudio.setPlayerState(false);
      this.pause();

      // Сбрасываем источник
      this.audio.src = "";
      this.setCurrentTime(0);

      // Очищаем обработчики
      this.audio.ontimeupdate = null;
      this.audio.oncanplay = null;
      this.audio.onended = null;
      this.audio.onloadedmetadata = null;
      this.audio.onerror = null;
      this.audio.onplay = null;
      this.audio.onpause = null;

      this.audio = null;
      this.useAudio.destroyedPlayer();
    }
  }

  /**
   *
   * @param type command name
   * @param value command value
   * @param command 'audio_control' - название контроллера (любое) в tv
   */
  sendCommand(
    type: string,
    value: string | number | boolean | unknown = "",
    command = "audio_control"
  ): void {
    this.$emit("sendNewCommand", command, { type, value });
  }
}
export default useAudioControl;
